import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useConfigFormState } from "../../../../common/ConfigForm/useConfigFormState";
import { hydrateNe1Form, buildNe1Payload } from "../ne1.payload";
// import { isSignWithoutMinimumDatasetEnabled, isUnableToAssessWoundEnabled, hasAtLeastOneSignCriterion, hasPatientRefusalReason } from "../ne1.rules";
import { useLoading } from "../../../../../hooks/useLoading";
import { LOADING_KEYS } from "../../../../../constants/loadingKeys";
import { SUCCESS_STATUS, ERROR_STATUS } from "../../../../../constants";
import { customerSaveAsync } from "../../../../../app/customerSlice";
import { scrollToTop } from "../../../../../utils";

export const useNe1 = ({ customerData }) => {
  const dispatch = useDispatch();
  const { register, handleSubmit, errors, reset, clearErrors } = useForm();

  const accessError = useSelector((state) => state.app.accessError);

  const { form, setForm, setField, toggleMulti } = useConfigFormState(() => hydrateNe1Form(customerData));

  const [editable, setEditable] = useState(false);
  const [disable, setDisable] = useState(false);
  const [reasonError, setReasonError] = useState(false);
  const [signOptionError, setSignOptionError] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  const [ne1State, setNe1State] = useState(customerData?.settings?.ne1Wms === 1 ? 1 : 0);
  const [makeShiftSold, setMakeShiftSold] = useState(true);
  const [popupSave, setPopupSave] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);

  const saveLoading = useLoading(LOADING_KEYS.UPDATE_CUSTOMER);

  useEffect(() => {
    if (customerData?.settings?.ne1Wms === 1) {
      setNe1State(1);
    } else {
      setNe1State(0);
    }
    setMakeShiftSold(true);
  }, [customerData]);

  useEffect(() => {
    if (!saveStatus) return;
    const timer = setTimeout(() => setSaveStatus(null), 5000);
    return () => clearTimeout(timer);
  }, [saveStatus]);

  const handleEdit = useCallback(() => {
    reset();
    setForm(hydrateNe1Form(customerData));
    setEditable((prev) => !prev);
    setDisable((prev) => !prev);
    setPopupSave(false);
    setNe1State(customerData?.settings?.ne1Wms === 1 ? 1 : 0);
    setMakeShiftSold(true);
    setShowConfirmPopup(false);
    setReasonError(false);
    setSignOptionError(false);
  }, [customerData, reset, setForm]);

  const onFieldChange = useCallback(
    (key, value) => {
      setField(key, value);

      if (key === "poaType") {
        clearErrors(["poa"]);
      }
      if (key === "signWithOutMinimumDataset" && value === 0) {
        setSignOptionError(false);
      }
      if (key === "unableToAssessWound" && value === 0) {
        setReasonError(false);
      }
    },
    [setField, clearErrors]
  );

  const onMultiChange = useCallback(
    (key, options) => {
      const selectedKeys = Array.isArray(options) ? options.map((item) => item.key) : [];
      setField(key, selectedKeys);

      if (key === "characteristics" || key === "measurements" || key === "treatment") {
        setSignOptionError(false);
      }
      if (key === "patientRefusalReasons") {
        setReasonError(false);
      }
    },
    [setField]
  );

  const handleClickStatus = useCallback(() => {
    setMakeShiftSold(true);
    setNe1State((prev) => (prev === 0 ? 1 : 0));
  }, []);

  const modalOpen = useCallback(() => setShowConfirmPopup(true), []);
  const modalRefresh = useCallback(() => setShowConfirmPopup(false), []);

  const handleSavePopup = useCallback(() => {
    setPopupSave(true);
    setShowConfirmPopup(false);
  }, []);

  const handleCancelPopup = useCallback(() => {
    setShowConfirmPopup(false);
    setMakeShiftSold(true);
  }, []);

  const doSubmit = useCallback(async () => {
    // if (isUnableToAssessWoundEnabled(form) && !hasPatientRefusalReason(form)) {
    //   setReasonError(true);
    //   return;
    // }

    // if (isSignWithoutMinimumDatasetEnabled(form) && !hasAtLeastOneSignCriterion(form)) {
    //   setSignOptionError(true);
    //   return;
    // }

    const result = buildNe1Payload({ form, customerData, ne1State, popupSave });
    try {
      await dispatch(customerSaveAsync(result));
      setEditable(false);
      reset();
      setSaveStatus(SUCCESS_STATUS);
      scrollToTop();
    } catch (error) {
      setSaveStatus(ERROR_STATUS);
    }
  }, [form, customerData, ne1State, popupSave, dispatch, reset]);

  return {
    register,
    handleSubmit,
    errors,
    form,
    onFieldChange,
    onMultiChange,
    toggleMulti,
    submitErrors: { reasonError, signOptionError },

    editable,
    disable,
    handleEdit,
    saveLoading,
    accessError,
    saveStatus,

    ne1State,
    makeShiftSold,
    popupSave,
    showConfirmPopup,
    handleClickStatus,
    modalOpen,
    modalRefresh,
    handleSavePopup,
    handleCancelPopup,

    doSubmit,
  };
};
